import { useSeo } from '@/lib/seo';
import Header from '@/components/Header';
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, RefreshCw, Radio, CloudRain, Truck, Camera, MonitorPlay, Clock, Waves } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

const ICONS = {
  nzta: <Radio className="w-5 h-5" />,
  metservice: <CloudRain className="w-5 h-5" />,
  restrictions: <Truck className="w-5 h-5" />,
  cameras: <Camera className="w-5 h-5" />,
  vms: <MonitorPlay className="w-5 h-5" />,
  journeys: <Clock className="w-5 h-5" />,
  geonet: <Waves className="w-5 h-5" />,
};

function intervalLabel(s) {
  if (!s) return '—';
  if (s >= 3600) return `every ${Math.round(s / 3600)}h`;
  if (s >= 60) return `every ${Math.round(s / 60)} min`;
  return `every ${s}s`;
}

export default function Status() {
  useSeo({ title: 'Live Data Feed Status', description: 'Real-time health of the NZTA Waka Kotahi, MetService and council data feeds powering PTT NZ Alerts.' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get('/status/feeds');
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const operational = data?.overall === 'operational';

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
        <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-1">System status</div>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <h1 className="font-display font-black text-3xl md:text-4xl">Live feed status</h1>
          <button onClick={load} className="btn btn-ghost" data-testid="status-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Overall banner */}
        {data && (
          <div
            className={`card mb-8 flex items-center gap-4 border-2 ${operational ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
            data-testid="status-overall"
          >
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${operational ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
              {operational ? <CheckCircle2 className="w-7 h-7" /> : <AlertTriangle className="w-7 h-7" />}
            </div>
            <div>
              <div className={`font-display font-black text-xl ${operational ? 'text-emerald-800' : 'text-amber-800'}`}>
                {operational ? 'All systems operational' : 'Some feeds are degraded'}
              </div>
              <div className="text-sm text-zinc-600">Auto-refreshes every 30s · last checked {dayjs(data.checked_at).format('HH:mm:ss')}</div>
            </div>
          </div>
        )}

        {loading && !data ? (
          <div className="card flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> Checking feeds…</div>
        ) : (
          <div className="grid gap-4">
            {data?.feeds?.map((f) => {
              const ok = f.online;
              return (
                <div key={f.key} className="card flex items-center gap-4" data-testid={`status-feed-${f.key}`}>
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${ok ? 'bg-teal-500/10 text-teal-600' : 'bg-amber-500/10 text-amber-600'}`}>
                    {ICONS[f.key] || <Radio className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg truncate">{f.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{f.provider} · polls {intervalLabel(f.poll_interval_seconds)}</div>
                    {f.last_error && <div className="text-xs text-red-600 mt-1 truncate">Last error: {f.last_error}</div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`inline-flex items-center gap-1.5 text-sm font-bold ${ok ? 'text-emerald-600' : 'text-amber-600'}`} data-testid={`status-indicator-${f.key}`}>
                      <span className={`w-2.5 h-2.5 rounded-full ${ok ? 'bg-emerald-500 pulse-red' : 'bg-amber-500'}`} />
                      {ok ? 'Online' : (f.status === 'starting' ? 'Starting' : 'Degraded')}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {f.last_poll ? `updated ${dayjs(f.last_poll).fromNow()}` : 'no data yet'}
                    </div>
                    {typeof f.records === 'number' && (
                      <div className="text-xs text-zinc-400 mt-0.5">{f.records.toLocaleString()} records</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-zinc-400 mt-8 text-center">
          Data © Waka Kotahi NZTA &amp; MetService · <Link to="/alerts" className="text-orange-600 hover:underline">View live alerts</Link>
        </p>
      </div>
    </div>
  );
}
